/**
 * `FallbackImg`'s repair handler and state (#443). Pure, with no `remotion`
 * import: testable without a DOM, and importable by the app without pulling
 * the renderer into every page.
 */

/**
 * The app's repair for dead images. `canRepair` says which URLs it knows; any
 * other image keeps Remotion's retries and is never sent. `repair` is asked
 * when one of its images fails: a different URL to swap in, the same URL when
 * the failure was transient, or `null` when there is none.
 */
export type ImageRepairHandler = {
  canRepair: (src: string) => boolean
  repair: (src: string) => Promise<string | null>
}

let repairHandler: ImageRepairHandler | undefined

/**
 * Registers the app's repair for dead images. A module singleton rather than
 * context, so it doesn't depend on the Remotion Player passing the app's
 * context into the composition. Nothing registers one on Lambda, where
 * `FallbackImg` behaves as it always has.
 */
export function setImageRepairHandler(handler: ImageRepairHandler | undefined) {
  repairHandler = handler
}

export const getImageRepairHandler = () => repairHandler

export type RepairState = {
  /** The `src` prop this state belongs to (O4). */
  for: string | null | undefined
  /** What to draw; `null` draws nothing. */
  url: string | null
  /** Bumped to remount `<Img>` and load the same URL once more. */
  attempt: number
  /** The handler was asked for this `src`; it is never asked twice. */
  asked: boolean
  /** Waiting for the handler; draws nothing meanwhile. */
  pending: boolean
}

export const initialRepairState = (
  src: string | null | undefined,
): RepairState => ({
  for: src,
  url: src || null,
  attempt: 0,
  asked: false,
  pending: false,
})

/**
 * The state for this render's `src`. A layout reuses one `FallbackImg` while
 * its speaker changes, so state made for another `src` starts over.
 */
export const repairStateFor = (
  stored: RepairState,
  src: string | null | undefined,
): RepairState => (stored.for === src ? stored : initialRepairState(src))

/** Whether the registered handler can repair `url`. */
export const canRepair = (url: string | null) =>
  Boolean(url && repairHandler?.canRepair(url))

/**
 * Remotion's retries before `onError`. An image the handler can repair asks it
 * on the first error: X's 404 for a deleted avatar is permanent, and four
 * retries wait 1 + 2 + 4 + 8 = 15 s first. Any other image — every image on
 * Lambda, and a thumbnail in the browser — keeps what it always had.
 */
export const maxRetriesFor = (repairable: boolean) => (repairable ? 0 : 4)

/** An image error: ask the handler (`ask: true`), or give up and draw nothing. */
export function onImageError(
  state: RepairState,
  repairable: boolean,
): { state: RepairState; ask: boolean } {
  if (!repairable || state.asked || !state.url) {
    return { state: { ...state, url: null, pending: false }, ask: false }
  }
  return { state: { ...state, asked: true, pending: true }, ask: true }
}

/**
 * The handler's answer for `askedFor`, whose image `failed`, applied to the
 * state stored for `askedFor`. `null` when the stored state already belongs to
 * another `src` (the component moved on and that one failed too), so the
 * answer must be dropped.
 *
 * It is applied even when the component is showing another `src` by now: the
 * stored state still belongs to `askedFor`, and dropping the answer would leave
 * it pending, so the image would stay blank if that `src` came back.
 */
export function onRepaired(
  prev: RepairState,
  {
    askedFor,
    failed,
    replacement,
  }: {
    askedFor: string | null | undefined
    failed: string
    replacement: string | null
  },
): RepairState | null {
  if (prev.for !== askedFor) return null
  if (!replacement) return { ...prev, url: null, pending: false }
  // The server found it live, so the error was transient: load it once more.
  if (replacement === failed)
    return { ...prev, attempt: prev.attempt + 1, pending: false }
  return { ...prev, url: replacement, pending: false }
}
